import { type ComponentProps, useRef } from 'react';

import { Transition, TransitionGroup } from 'react-transition-group';

import { Box } from '@change-corgi/design-system/layout';
import type { ColorName } from '@change-corgi/design-system/theme';
import { derivedStyle } from '@change-corgi/design-system/theme';

import type { HeroFeedItem as HeroFeedItemType } from 'src/app/pages/psf/promoteOrShare/pageContext';

import { HeroFeedItem, ITEM_MIN_HEIGHT } from './components/HeroFeedItem';
import { useHeroFeed } from './hooks/useHeroFeed';

type Props = {
	items: readonly HeroFeedItemType[];
	animationStartSize: number;
	noPadding?: boolean;
};

export function HeroFeed({
	items,
	animationStartSize,
	noPadding,
	backgroundColor,
	...rest
}: Props & ComponentProps<typeof Box>) {
	const { data } = useHeroFeed(items, animationStartSize);
	const nodeRef = useRef<HTMLDivElement>(null);

	const padding = noPadding ? 0 : 16;

	return (
		<Box
			sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}
			backgroundColor={backgroundColor}
			p={padding}
			{...rest}
		>
			<Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }} p={padding}>
				<TransitionGroup component="ul">
					{data.items.map((item) => (
						<Transition key={item.event.id} nodeRef={nodeRef} timeout={data.animateSpeed}>
							{(state) => (
								<HeroFeedItem
									item={item}
									as="li"
									mb={16}
									sx={{
										transition: `margin-top ${data.animateSpeed}ms ease-in-out`,
										mt:
											state === 'entering'
												? -1 * (nodeRef.current ? nodeRef.current.clientHeight : ITEM_MIN_HEIGHT)
												: 0,
										visibility: state === 'entered' ? 'visible' : 'hidden',
									}}
								/>
							)}
						</Transition>
					))}
				</TransitionGroup>
			</Box>
			<Box
				sx={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					width: '100%',
					height: 30,
					background: derivedStyle(
						(theme) =>
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							`linear-gradient(rgba(255, 255, 255, 0) 0%, ${theme.colors[typeof backgroundColor === 'string' ? (backgroundColor as ColorName) : 'primary-white'] || '#ffffff'} 100%)`,
					),
				}}
			></Box>
		</Box>
	);
}
